// HTML overlay for the fragment draft. Renders a card-style hand drawn from
// FragmentManager, wires hover pop animations, and resolves the user's pick.
import {
  hideOverlay,
  showOverlay,
  fragmentChipEnter,
  fragmentCardHoverIn,
  fragmentCardHoverOut,
  animateDraftHeader,
} from './animator.js';

function getFragmentImagePath(fragmentId) {
  if (!fragmentId) return '';
  return `/assets/2D/IconFragments/${fragmentId}.png`;
}

function colorHex(n) {
  return '#' + (n >>> 0).toString(16).padStart(6, '0');
}

// Lighten/darken a 0xRRGGBB number by mixing with white/black.
function tintHex(n, amount) {
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  const t = Math.max(-1, Math.min(1, amount));
  const mix = (c) => {
    const target = t >= 0 ? 255 : 0;
    const v = Math.round(c + (target - c) * Math.abs(t));
    return Math.max(0, Math.min(255, v));
  };
  return colorHex((mix(r) << 16) | (mix(g) << 8) | mix(b));
}

function rgba(n, alpha) {
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export class FragmentDraftUI {
  constructor() {
    this.el = document.getElementById('fragment-draft');
    this.cards = document.getElementById('draft-cards');
    this.titleMeta = this.el?.querySelector('.draft-title-meta') || null;
    this.title = this.el?.querySelector('.draft-title') || null;
    this.sub = this.el?.querySelector('.draft-sub') || null;
    this._resolve = null;
    this._cleanups = [];
    this.audio = null;
  }

  setAudio(audio) {
    this.audio = audio || null;
  }

  show(choices) {
    this._teardownCards();
    this.cards.innerHTML = '';

    for (const f of choices) {
      const card = this._buildCard(f);
      this.cards.appendChild(card);
      fragmentChipEnter(card, { delay: this.cards.children.length * 0.05 });
    }

    showOverlay(this.el);
    animateDraftHeader(this.titleMeta, this.title, this.sub);
    this.audio?.play('cardFan', { rate: 0.98 + Math.random() * 0.05 });
    return new Promise((res) => {
      this._resolve = res;
    });
  }

  _buildCard(f) {
    const accent = colorHex(f.color);
    const accentDark = tintHex(f.color, -0.55);
    const accentSoft = rgba(f.color, 0.18);
    const initial = (f.name?.[0] || '?').toUpperCase();
    const imgSrc = getFragmentImagePath(f.id);

    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'frag-card';
    card.style.setProperty('--card-accent', accent);
    card.style.setProperty('--card-accent-soft', accentSoft);
    card.dataset.fragmentId = f.id;

    card.innerHTML = `
      <div class="frag-card-frame">
        <div class="frag-card-header" style="background:${accent};color:${accentDark}">
          <span class="frag-card-name">${f.name.toUpperCase()}</span>
        </div>
        <div class="frag-card-art" style="background:radial-gradient(ellipse at center, ${rgba(f.color, 0.32)} 0%, ${rgba(f.color, 0.08)} 60%, rgba(3, 15, 13, 0.85) 100%)">
          <img class="frag-card-art-img" alt="" ${imgSrc ? `src="${imgSrc}"` : ''} ${imgSrc ? '' : 'hidden'} />
          <div class="frag-card-art-placeholder" ${imgSrc ? 'hidden' : ''} aria-hidden="true">
            <span class="frag-card-art-mono" style="color:${accent}">${initial}</span>
            <span class="frag-card-art-grid" style="border-color:${rgba(f.color, 0.35)}"></span>
          </div>
          <div class="frag-card-kind" style="color:${accent};border-color:${rgba(f.color, 0.45)}">${f.kind}</div>
        </div>
        <div class="frag-card-desc">${f.desc}</div>
      </div>
    `;
    const imgEl = card.querySelector('.frag-card-art-img');
    const placeholderEl = card.querySelector('.frag-card-art-placeholder');
    if (imgEl && placeholderEl) {
      imgEl.addEventListener('error', () => {
        imgEl.hidden = true;
        placeholderEl.hidden = false;
      });
      imgEl.addEventListener('load', () => {
        imgEl.hidden = false;
        placeholderEl.hidden = true;
      });
    }

    const onClick = () => this._pick(f.id);
    const onEnter = () => {
      fragmentCardHoverIn(card);
      this.audio?.play('cardHover', { rate: 1.02 + Math.random() * 0.08 });
    };
    const onLeave = () => fragmentCardHoverOut(card);
    const onFocus = () => {
      fragmentCardHoverIn(card);
      this.audio?.play('cardHover', { rate: 1.02 + Math.random() * 0.08 });
    };
    const onBlur = () => fragmentCardHoverOut(card);

    card.addEventListener('click', onClick);
    card.addEventListener('pointerenter', onEnter);
    card.addEventListener('pointerleave', onLeave);
    card.addEventListener('focus', onFocus);
    card.addEventListener('blur', onBlur);

    this._cleanups.push(() => {
      card.removeEventListener('click', onClick);
      card.removeEventListener('pointerenter', onEnter);
      card.removeEventListener('pointerleave', onLeave);
      card.removeEventListener('focus', onFocus);
      card.removeEventListener('blur', onBlur);
    });

    return card;
  }

  _teardownCards() {
    while (this._cleanups.length) {
      try { this._cleanups.pop()(); } catch { /* ignore */ }
    }
  }

  _pick(id) {
    if (!this._resolve) return;
    hideOverlay(this.el, { duration: 0.18 });
    const r = this._resolve;
    this._resolve = null;
    this._teardownCards();
    r(id);
  }
}
